import {useAtom} from 'jotai';
import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Typography} from '@/components/Typography';
import {AnalysisItem, BusStopData, SubwayData} from '@/map/@types';
import {useBusSubwayByPoint} from '@/map/api';
import {selectedNearbyInfoItemCardAtom} from '@/map/atom';
import {
  AROUND_DATA_OPTIONS,
  MIN_SUBWAY_PASSENGERS_COUNT,
  SUBWAY_LINE_COLORS,
} from '@/map/constants';
import {AreaAnalyticsFilter} from '@/map/enums';
import {ColumnFlexBox, Flexbox} from '@/styled-components';
import {palette} from '@/styles';
import {withCommaWithUnit} from '@/utils';
import {SpotRoundMark} from '../../../Marker';
import {
  BusStopMarkers,
  SubwayStationMarkers,
} from '../../../MarkerCluster/NearbyInfo';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {ColorCircle, ReportContainer} from '../StyledComponents';
import {NearbyInfoTable} from './Table';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
}

export const BusSubwayReport = ({map, item}: IProps) => {
  const [activeItem, setActiveItem] = useAtom(selectedNearbyInfoItemCardAtom);

  const {data, isPending, error} = useBusSubwayByPoint(item);

  const tableData = useMemo(() => {
    if (data) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        if (key === AreaAnalyticsFilter.BUS) {
          acc[key] = (value as BusStopData[]).map((i) => [
            i.stopName,
            withCommaWithUnit(i.avgNumberOfRidesPerDay, {
              placeholder: '정보 없음',
              unit: '명',
            }),
          ]);
        } else if (key === AreaAnalyticsFilter.SUBWAY) {
          acc[key] = (value as SubwayData[])
            .map((i) => {
              const subway = i.subways?.[0];

              if (
                !subway ||
                (subway.getOnOffAvg &&
                  subway.getOnOffAvg <= MIN_SUBWAY_PASSENGERS_COUNT)
              ) {
                return undefined;
              }

              return [
                <SubwayNameCell>
                  <ColorCircle
                    color={SUBWAY_LINE_COLORS[subway.line] || palette.gray300}
                  />
                  <Typography ellipsis>{i.name}</Typography>
                </SubwayNameCell>,
                subway.line,
                withCommaWithUnit(subway.getOnOffAvg, {
                  placeholder: '-',
                  unit: '명',
                }),
              ];
            })
            .filter((i) => !!i);
        }

        return acc;
      }, {} as any);
    }
  }, [data]);

  const onMarkerClick = useCallback(
    ({
      type,
      index,
    }: {
      type: AreaAnalyticsFilter.BUS | AreaAnalyticsFilter.SUBWAY;
      index: number;
    }) => {
      if (!data) {
        return;
      }

      const item = data[type]?.[index];

      setActiveItem((prev) => {
        if (prev?.type === type && isEqual(item, prev.item)) {
          return null;
        }

        return {
          type,
          item,
        };
      });
    },
    [data, setActiveItem]
  );

  useEffect(() => {
    return () => {
      setActiveItem(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <ReportContainer>
        <ErrorBox />
      </ReportContainer>
    );
  } else if (isPending) {
    return (
      <ReportContainer>
        <LoadingBar color={palette.textSecondary} />
      </ReportContainer>
    );
  }

  const noData =
    tableData &&
    !tableData[AreaAnalyticsFilter.BUS]?.length &&
    !tableData[AreaAnalyticsFilter.SUBWAY]?.length;

  if (noData) {
    return (
      <ReportContainer>
        <NoData text="분석 지역 내 대중교통 정보가 없습니다." />
      </ReportContainer>
    );
  }

  return (
    <>
      <BusStopMarkers
        map={map}
        items={data?.[AreaAnalyticsFilter.BUS]}
        selectedItem={
          activeItem?.type === AreaAnalyticsFilter.BUS
            ? activeItem.item
            : undefined
        }
        onClick={onMarkerClick}
      />
      <SubwayStationMarkers
        map={map}
        items={data?.[AreaAnalyticsFilter.SUBWAY]}
        selectedItem={
          activeItem?.type === AreaAnalyticsFilter.SUBWAY
            ? activeItem.item
            : undefined
        }
        onClick={onMarkerClick}
      />
      <ReportContainer>
        <ColumnFlexBox gap={40}>
          <NearbyInfoTable
            title="지하철"
            headers={[
              {width: '40%', label: '역 이름'},
              {width: '30%', label: '호선'},
              {width: '30%', label: '일 평균 승하차'},
            ]}
            sumLabel="지하철역이"
            icon={<Icon type={AreaAnalyticsFilter.SUBWAY} />}
            data={tableData?.[AreaAnalyticsFilter.SUBWAY]}
          />
          <NearbyInfoTable
            title="버스"
            headers={[
              {width: '60%', label: '정류장'},
              {width: '40%', label: '일 평균 승하차'},
            ]}
            sumLabel="버스 정류장이"
            icon={<Icon type={AreaAnalyticsFilter.BUS} />}
            data={tableData?.[AreaAnalyticsFilter.BUS]}
            maxLineCount={2}
          />
        </ColumnFlexBox>
      </ReportContainer>
    </>
  );
};

const Icon = ({type}: {type: AreaAnalyticsFilter}) => {
  const options = AROUND_DATA_OPTIONS[type];

  return <SpotRoundMark typeLabel={options.label} {...options.mobile} />;
};

const SubwayNameCell = styled(Flexbox)`
  gap: 8px;
`;
